import { useEffect, useRef, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  BookOpen,
  ChevronDown,
  Clock3,
  ExternalLink,
  RefreshCw,
  Search,
} from "lucide-react";
import {
  Badge,
  Button,
  DataChart,
  DistributionPlot,
  Field,
  FilterBar,
  SegmentedControl,
  Select,
  Table,
} from "../../design-system";
import { Panel } from "./shared";
import { SeoNotice } from "./seo-shared";
import {
  competitionAreas,
  competitionFixture,
  competitionWords,
  sourceFixture,
  type Benchmark,
  type CompetitionExample,
  type CompetitionItem,
  type CompetitionSources,
  type CompetitionView,
  type PageStatus,
} from "./competition-data";
import "./competition.css";

const number = (v: number | null | undefined, unit = "") =>
  v == null
    ? "—"
    : v.toLocaleString("ko-KR", { maximumFractionDigits: 1 }) + unit;
const statusLabels: Record<PageStatus, string> = {
  ok: "분석됨",
  pending: "받는 중",
  failed: "수집 실패",
  blocked: "수집 금지",
  unsupported: "로그인 필요",
  gone: "없음 · 비공개",
};
const statusNotes: Record<PageStatus, string> = {
  ok: "공개 페이지에서 센 값",
  pending: "글을 받으면 지표가 자동으로 채워집니다",
  failed: "응답을 받지 못했습니다. 다음 재시도 예정",
  blocked: "robots 정책으로 수집하지 않습니다",
  unsupported: "로그인이 필요한 글은 수집하지 않습니다",
  gone: "삭제되었거나 비공개인 글입니다",
};
const timestamp = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat("ko-KR", {
        timeZone: "Asia/Seoul",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(value))
    : "첫 수집 전";

function Empty({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="competition-empty">
      <Search size={28} />
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  );
}
function PageState({ item }: { item: CompetitionItem }) {
  const status = item.page?.status ?? "pending";
  return (
    <span className="competition-state" title={statusNotes[status]}>
      <Badge
        tone={
          status === "ok"
            ? "teal"
            : status === "pending"
              ? "blue"
              : status === "failed"
                ? "coral"
                : "neutral"
        }
      >
        {statusLabels[status]}
      </Badge>
      <small>{status !== "ok" && statusNotes[status]}</small>
    </span>
  );
}

export function CompetitionSourceBoard({
  data,
  onKeyword,
}: {
  data: CompetitionSources;
  onKeyword: (word: string) => void;
}) {
  const [all, setAll] = useState(false);
  const max = Math.max(1, ...data.items.map((s) => s.appearances));
  return (
    <Panel
      title="경쟁 현황판"
      extra={
        <Badge>
          {data.keywords}개 키워드 · 수집 {data.collectedDays}일
        </Badge>
      }
    >
      <p className="competition-caption">
        최근 {data.days}일 · {competitionAreas[data.area]} 상위 {data.top}에
        등장한 작성처입니다. 등장 횟수는 키워드 × 날짜 기준입니다.
      </p>
      {!data.items.length ? (
        <Empty title="기간 안 수집 결과가 없습니다">
          키워드의 첫 수집이 끝나면 작성처별 현황이 표시됩니다.
        </Empty>
      ) : (
        <>
          <div className="competition-source-table">
            <Table>
              <thead>
                <tr>
                  <th>작성처 · 주제</th>
                  <th>등장 / 그중 3위 안</th>
                  <th>키워드</th>
                  <th>평균 / 최고 순위</th>
                  <th>마지막 등장</th>
                </tr>
              </thead>
              <tbody>
                {data.items.slice(0, all ? undefined : 5).map((s) => (
                  <tr
                    key={s.source}
                    className={s.ours ? "competition-ours" : ""}
                  >
                    <td>
                      <details>
                        <summary>
                          <ChevronDown size={14} />
                          <span>
                            <b>{s.name}</b>
                            <small>{s.topic ?? "주제 정보 없음"}</small>
                          </span>
                          {s.ours && <Badge tone="blue">우리 자산</Badge>}
                        </summary>
                        <div className="competition-keywords">
                          {s.keywordList.map((w) => (
                            <button
                              key={w}
                              type="button"
                              onClick={() => onKeyword(w)}
                            >
                              {w}
                              <ArrowDownRight size={13} />
                            </button>
                          ))}
                        </div>
                      </details>
                    </td>
                    <td>
                      <div className="competition-count">
                        <b>{s.appearances}</b>
                        <span>/ {s.top3}</span>
                      </div>
                      <div
                        className="competition-bar"
                        aria-label={
                          "상위 10 등장 " +
                          s.appearances +
                          "회, 그중 3위 안 " +
                          s.top3 +
                          "회"
                        }
                      >
                        <i
                          style={{ width: (s.appearances / max) * 100 + "%" }}
                        />
                        <b style={{ width: (s.top3 / max) * 100 + "%" }} />
                      </div>
                    </td>
                    <td>{s.keywords}개</td>
                    <td>
                      {number(s.avgRank)}위{" "}
                      <span className="competition-caption">
                        / {s.bestRank}위
                      </span>
                    </td>
                    <td>{s.lastDay.slice(5).replace("-", ".")}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <footer className="competition-source-footer">
            <span>
              <i /> 상위 10 등장 <b /> 그중 3위 안 · 평균은 등장한 날 기준
            </span>
            {data.items.length > 5 && (
              <Button size="sm" variant="ghost" onClick={() => setAll(!all)}>
                {all ? "접기" : "작성처 " + data.items.length + "개 모두 보기"}
                <ChevronDown size={14} />
              </Button>
            )}
          </footer>
        </>
      )}
    </Panel>
  );
}

export function CompetitionBenchmark({
  rows,
  ours = true,
}: {
  rows: Benchmark[];
  ours?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const core = ["chars", "images", "headings", "kwCount", "kwTitle", "ageDays"];
  const ordered = [...rows].sort(
    (a, b) =>
      (core.includes(a.key) ? core.indexOf(a.key) : 99) -
      (core.includes(b.key) ? core.indexOf(b.key) : 99),
  );
  return (
    <div className="competition-benchmark">
      <div className="competition-section-intro">
        <div>
          <h3>
            {ours ? "상위 글의 공통점과 우리 글과의 차이" : "상위 글의 공통점"}
          </h3>
          <p>
            공개 페이지에서 센 값을 비교합니다. 값이 크거나 작다는 이유만으로
            유리함을 판단하지 않습니다.
          </p>
        </div>
        <Badge>핵심 6개 / 전체 14개</Badge>
      </div>
      <div className="competition-legend">
        <span className="top-dot" /> 상위 3 글 <span className="normal-dot" />{" "}
        4~10위 글 <span className="median-mark" /> 상위 10 중앙값{" "}
        {ours && (
          <>
            <span className="our-mark" /> 우리 글
          </>
        )}
      </div>
      <p className="competition-caption">
        각 지표는 독립된 축입니다. 제목·첫 문단은 포함 비율이며 세로선은 평균
        비율을 표시합니다. 수집되지 않은 값은 제외합니다.
      </p>
      <div
        className={"competition-bench-head " + (!ours ? "without-ours" : "")}
      >
        <span>지표 · 분석 표본</span>
        <span>개별 글 분포</span>
        <span>상위 3</span>
        <span>상위 10</span>
        {ours && (
          <>
            <span>우리 글</span>
            <span>차이</span>
          </>
        )}
      </div>
      {(expanded ? ordered : ordered.slice(0, 6)).map((row) => (
        <article
          key={row.key}
          className={"competition-bench-row " + (!ours ? "without-ours" : "")}
          aria-label={row.label}
        >
          <div className="competition-metric-name">
            <b>{row.label}</b>
            <small>
              {row.samples}개 글 분석 ·{" "}
              {row.ratio ? "포함 비율" : "중앙값 비교"}
            </small>
          </div>
          <DistributionPlot
            label={row.label}
            samples={row.values.map((v) => ({
              label: v.rank + "위 글",
              value: v.value,
              highlighted: v.rank <= 3,
            }))}
            median={row.top10}
            medianLabel={row.ratio ? "상위 10 포함 비율" : "상위 10 중앙값"}
            reference={ours ? row.ours : undefined}
            referenceLabel="우리 글"
            maximum={row.ratio ? 100 : undefined}
            format={(v) => number(v, row.unit)}
          />
          <div className="competition-bench-value">
            <small>상위 3</small>
            {number(row.top3, row.unit)}
          </div>
          <div className="competition-bench-value">
            <small>상위 10</small>
            {number(row.top10, row.unit)}
          </div>
          {ours && (
            <>
              <div className="competition-bench-value is-ours">
                <small>우리 글</small>
                {number(row.ours, row.unit)}
              </div>
              <div className="competition-bench-value">
                <small>차이</small>
                {row.diff == null
                  ? "—"
                  : (row.diff > 0 ? "+" : "") +
                    number(row.diff, row.ratio ? "%p" : row.unit)}
              </div>
            </>
          )}
        </article>
      ))}
      <Button variant="ghost" onClick={() => setExpanded(!expanded)}>
        {expanded ? "핵심 지표만 보기" : "지표 8개 더 보기"}
        <ChevronDown size={15} />
      </Button>
      <p className="competition-caption">
        상위 3·10은 분석 가능한 글만 집계합니다.
        {ours &&
          " 우리 글은 조회 범위 안 가장 높은 순위의 글입니다. 차이 = 우리 글 − 상위 10."}
      </p>
    </div>
  );
}
const postMetrics = (item: CompetitionItem) => {
  const p = item.page,
    m = p?.metrics,
    k = p?.keyword;
  return [
    ["글자", number(m?.chars, "자")],
    ["사진", number(m?.images, "개")],
    ["소제목", number(m?.headings, "개")],
    ["태그", number(m?.tags.length, "개")],
    ["댓글", number(m?.comments, "개")],
    ["키워드", number(k?.count, "회")],
    ["제목", k == null ? "—" : k.inTitle ? "포함" : "미포함"],
    ["첫 문단", k == null ? "—" : k.inFirst ? "포함" : "미포함"],
  ];
};
export function CompetitionPosts({
  items,
  ours = true,
}: {
  items: CompetitionItem[];
  ours?: boolean;
}) {
  return (
    <>
      <div className="competition-post-table">
        <Table>
          <thead>
            <tr>
              {[
                "순위 / 글",
                "작성처 · 주제",
                "게시 / 글 나이",
                "글자",
                "사진",
                "소제목",
                "태그",
                "댓글",
                "키워드",
                "제목",
                "첫 문단",
                "수집",
              ].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.link}
                className={ours && item.our ? "competition-ours" : ""}
              >
                <td>
                  <div className="competition-post-title">
                    <Badge>{item.rank}위</Badge>
                    {ours && item.our && <Badge tone="blue">우리 글</Badge>}
                    <a href={item.link} target="_blank" rel="noreferrer">
                      {item.title}
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </td>
                <td>
                  {item.name}
                  <small>{item.page?.topic ?? "—"}</small>
                </td>
                <td>
                  {item.date.slice(5)}
                  <small>{number(item.page?.ageDays, "일")}</small>
                </td>
                {postMetrics(item).map(([label, v]) => (
                  <td key={label}>{v}</td>
                ))}
                <td>
                  <PageState item={item} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div className="competition-post-cards">
        {items.map((item) => (
          <article
            key={item.link}
            className={ours && item.our ? "competition-ours" : ""}
          >
            <header>
              <Badge>{item.rank}위</Badge>
              {ours && item.our && <Badge tone="blue">우리 글</Badge>}
              <PageState item={item} />
            </header>
            <a href={item.link} target="_blank" rel="noreferrer">
              {item.title}
              <ExternalLink size={13} />
            </a>
            <p>
              {item.name} · {item.page?.topic ?? "주제 없음"}
            </p>
            <p>
              {item.date} · 글 나이 {number(item.page?.ageDays, "일")}
            </p>
            <dl>
              {postMetrics(item).map(([label, v]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>
      <p className="competition-caption">
        — 는 수집되지 않은 값입니다. 댓글 0과 구분합니다. 로그인 필요·수집
        금지·없음 상태는 비교 집계에서 제외합니다.
      </p>
    </>
  );
}

export function KeywordCompetitionView({
  view,
  free = false,
  canRefresh = false,
  onRefresh,
  refreshing = false,
}: {
  view: CompetitionView;
  free?: boolean;
  canRefresh?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
}) {
  const [tab, setTab] = useState("compare");
  const ready = view.items.filter((i) => i.page?.status === "ok").length;
  return (
    <section className="competition-result" aria-label="키워드 경쟁 분석">
      <header className="competition-result-head">
        <div>
          <span className="competition-eyebrow">
            {competitionAreas[view.area]} · 키워드 경쟁 분석
          </span>
          <h2>{view.keyword}</h2>
          <p>
            수집 {timestamp(view.collectedAt)} · 결과 {number(view.total, "건")}{" "}
            · 조회 상위 {number(view.depth)}
          </p>
        </div>
        {!free && canRefresh && (
          <Button
            variant="secondary"
            size="sm"
            disabled={refreshing || view.collectedAt == null}
            onClick={onRefresh}
          >
            <RefreshCw size={14} />
            {refreshing ? "분석 중" : "다시 분석"}
          </Button>
        )}
      </header>
      {!view.collectedAt ? (
        <Empty title="첫 번째 검색 결과를 기다리고 있습니다">
          다음 수집 예정 10:00 · 수집을 마치면 비교할 글과 작성처가 표시됩니다.
        </Empty>
      ) : (
        <>
          <div className="competition-summary">
            {!free && (
              <div>
                <span>우리 최고 순위</span>
                <strong>{number(view.ourBestRank, "위")}</strong>
                <small>
                  {view.ourBestRank != null && view.ourBestRank > 10
                    ? "상위 10 밖 · 비교에 우리 글 포함"
                    : "조회 범위 내 최고 순위"}
                </small>
              </div>
            )}
            <div>
              <span>분석된 글</span>
              <strong>
                {ready}
                <em> / {view.items.length}</em>
              </strong>
              <small>공개 페이지 지표</small>
            </div>
            <div>
              <span>받는 중</span>
              <strong>
                {view.pending}
                <em>개</em>
              </strong>
              <small>
                {view.pending ? "자동으로 결과를 갱신합니다" : "현재 수집 완료"}
              </small>
            </div>
          </div>
          {view.pending > 0 && (
            <div role="status" className="competition-progress">
              <RefreshCw size={15} />
              <span>
                <b>{view.pending}개 받는 중</b> · 분석 가능한 글부터 표시합니다.
                3초마다 확인합니다.
              </span>
              <progress
                value={ready}
                max={view.items.length}
                aria-label="공개 글 분석 진행"
              />
            </div>
          )}
          <div className="competition-tabs">
            <SegmentedControl
              label="경쟁 분석 상세"
              value={tab}
              onChange={setTab}
              options={[
                { value: "compare", label: "공통점과 차이" },
                { value: "posts", label: "상위 글" },
                ...(!free
                  ? [{ value: "history", label: "순위 추이 · 변화" }]
                  : []),
              ]}
            />
          </div>
          {tab === "compare" && (
            <CompetitionBenchmark rows={view.benchmark} ours={!free} />
          )}
          {tab === "posts" && (
            <CompetitionPosts items={view.items} ours={!free} />
          )}
          {tab === "history" && !free && (
            <div className="competition-history">
              {view.history && (
                <DataChart
                  title="작성처별 순위 추이"
                  labels={view.history.labels}
                  series={view.history.series.map((s) => ({
                    name: s.name,
                    values: s.values.map((v) =>
                      v == null || v > (view.depth ?? 100) ? null : v,
                    ),
                    missing: s.values.map((v) =>
                      v != null && v > (view.depth ?? 100)
                        ? "상위 " + view.depth + " 밖"
                        : null,
                    ),
                  }))}
                  rank
                  description="최신 상위 10 작성처와 우리 자산을 비교합니다. 우리 블로그는 한 줄로 묶고 미수집 구간은 연결하지 않습니다."
                />
              )}
              <div className="competition-changes">
                {view.changes ? (
                  (
                    [
                      {
                        key: "entered",
                        title: "상위 10에 새로 들어온 글",
                        icon: <ArrowUpRight size={17} />,
                      },
                      {
                        key: "left",
                        title: "상위 10에서 빠진 글",
                        icon: <ArrowDownRight size={17} />,
                      },
                    ] as const
                  ).map((group) => (
                    <section key={group.key}>
                      <h3>
                        {group.icon}
                        {group.title}
                      </h3>
                      <p className="competition-caption">
                        {timestamp(view.changes!.previousAt)} 수집 대비
                      </p>
                      {view.changes![group.key].length ? (
                        view.changes![group.key].map((item) => (
                          <article key={item.link}>
                            <span>
                              {number(item.previousRank, "위")} →{" "}
                              {number(item.rank, "위")}
                            </span>
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {item.title}
                            </a>
                            <small>{item.name}</small>
                          </article>
                        ))
                      ) : (
                        <p>변화한 글이 없습니다.</p>
                      )}
                    </section>
                  ))
                ) : (
                  <Empty title="비교할 이전 수집이 없습니다">
                    첫날 데이터입니다. 다음 수집부터 진입·이탈을 확인할 수
                    있습니다.
                  </Empty>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Memory-only preview controller. Presentational exports above receive API-shaped data. */
export function SeoCompetition({
  example = "normal",
  canRefresh = true,
}: {
  example?: CompetitionExample;
  canRefresh?: boolean;
}) {
  const [mode, setMode] = useState(
    example.startsWith("free-") ? "probe" : "customer",
  );
  const [customer, setCustomer] = useState("onyou"),
    [area, setArea] = useState("blog"),
    [days, setDays] = useState(30),
    [keyword, setKeyword] = useState("");
  const [draft, setDraft] = useState(
      example.startsWith("free-") ? "성수 브런치" : "",
    ),
    [submitted, setSubmitted] = useState(
      example.startsWith("free-") ? "성수 브런치" : "",
    );
  const [error, setError] = useState(
    example === "free-failed"
      ? "이 연결의 API 한도에 도달했습니다. 다음 달 1일 한도가 갱신되면 조회할 수 있습니다."
      : example === "free-no-key"
        ? "사용 가능한 검색 API 연결이 없습니다. SEO 연결을 확인해 주세요."
        : "",
  );
  const [formError, setFormError] = useState(""),
    [running, setRunning] = useState(false),
    [refreshing, setRefreshing] = useState(false);
  const [recent, setRecent] = useState([
    {
      word: "성수 브런치",
      area: "blog",
      total: 18420,
      time: "10.20 08:40",
      by: "김*서",
    },
    {
      word: "역삼 건강검진",
      area: "web",
      total: 2340,
      time: "10.19 16:20",
      by: "박*민",
    },
  ]);
  const resultRef = useRef<HTMLDivElement>(null);
  const words = competitionWords(customer),
    selected = keyword || words[0];
  useEffect(() => {
    if (!running && !refreshing) return;
    const t = setTimeout(() => {
      setRunning(false);
      setRefreshing(false);
    }, 3000);
    return () => clearTimeout(t);
  }, [running, refreshing]);
  const choose = (word: string) => {
    setKeyword(word);
    resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    resultRef.current?.focus({ preventScroll: true });
  };
  const changeArea = (next: string) => {
    setArea(next);
    setKeyword("");
    setSubmitted("");
    setError("");
    setRunning(false);
  };
  const empty = example === "empty" || customer === "bom";
  const activeExample = refreshing ? "pending" : example;
  const view = competitionFixture(
    customer,
    area,
    days,
    mode === "probe" ? submitted : selected,
    mode === "probe"
      ? running
        ? "free-pending"
        : example === "free-pending"
          ? "free-pending"
          : "normal"
      : activeExample,
    mode === "probe",
  );
  return (
    <div className="competition-screen">
      <div className="competition-toolbar">
        <div className="competition-mode">
          <span>분석 방식</span>
          <SegmentedControl
            label="분석 방식"
            value={mode}
            onChange={setMode}
            options={[
              { value: "customer", label: "고객사 키워드" },
              { value: "probe", label: "자유 분석" },
            ]}
          />
        </div>
        <FilterBar>
          {mode === "customer" && (
            <Select
              label="고객사"
              value={customer}
              onChange={(e) => {
                setCustomer(e.target.value);
                setKeyword("");
              }}
            >
              <option value="onyou">온유의원</option>
              <option value="moru">모루식당</option>
              <option value="bom">봄날스튜디오</option>
            </Select>
          )}
          <Select
            label="검색 영역"
            value={area}
            onChange={(e) => changeArea(e.target.value)}
          >
            {Object.entries(competitionAreas).map(([v, n]) => (
              <option value={v} key={v}>
                {n}
              </option>
            ))}
          </Select>
          {mode === "customer" && (
            <Select
              label="조회 기간"
              value={String(days)}
              onChange={(e) => setDays(+e.target.value)}
            >
              {[7, 30, 90].map((d) => (
                <option value={d} key={d}>
                  최근 {d}일
                </option>
              ))}
            </Select>
          )}
        </FilterBar>
      </div>
      <SeoNotice>
        검색 API 기준 순위이며 네이버 화면 순서와 다를 수 있음
      </SeoNotice>
      {mode === "customer" ? (
        empty ? (
          <Panel title="고객사 키워드">
            <Empty title="추적 중인 키워드가 없습니다">
              게시 작업 등록 때 키워드를 입력하면 추적이 시작됩니다.
            </Empty>
          </Panel>
        ) : (
          <>
            <CompetitionSourceBoard
              key={customer + area + days}
              data={sourceFixture(customer, area, days, example)}
              onKeyword={choose}
            />
            <div
              ref={resultRef}
              tabIndex={-1}
              className="competition-detail-focus"
            >
              <div className="competition-keyword-picker">
                <BookOpen size={18} />
                <Select
                  label="분석할 키워드"
                  value={selected}
                  onChange={(e) => setKeyword(e.target.value)}
                >
                  {words.map((w) => (
                    <option key={w}>{w}</option>
                  ))}
                </Select>
              </div>
              <KeywordCompetitionView
                key={customer + area + selected}
                view={view}
                canRefresh={canRefresh}
                onRefresh={() => setRefreshing(true)}
                refreshing={refreshing}
              />
            </div>
          </>
        )
      ) : (
        <>
          <Panel title="자유 분석" extra={<Badge>등록하지 않은 키워드</Badge>}>
            <form
              className="competition-probe-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!draft.trim()) {
                  setFormError("조회할 키워드를 입력해 주세요.");
                  return;
                }
                setFormError("");
                if (example === "free-failed" || example === "free-no-key") {
                  setError(
                    example === "free-failed"
                      ? "이 연결의 API 한도에 도달했습니다. 다음 달 1일 한도가 갱신되면 조회할 수 있습니다."
                      : "사용 가능한 검색 API 연결이 없습니다. SEO 연결을 확인해 주세요.",
                  );
                  return;
                }
                setError("");
                setSubmitted(draft.trim());
                setRunning(true);
                setRecent((r) => [
                  {
                    word: draft.trim(),
                    area,
                    total: 18420,
                    time: "10.20 09:00",
                    by: "나",
                  },
                  ...r,
                ]);
              }}
            >
              <Field
                label="키워드"
                placeholder="예: 성수 브런치"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setFormError("");
                }}
                error={formError}
                maxLength={100}
              />
              <Button type="submit" disabled={running}>
                <Search size={15} />
                {running ? "조회 중" : "조회"}
              </Button>
            </form>
            <p className="competition-caption">
              선택한 검색 영역을 조회할 때 검색 API 한도 1회를 사용합니다. 글
              지표는 순서대로 수집합니다.
            </p>
            {error && (
              <div className="competition-error" role="alert">
                <strong>자유 분석을 조회하지 못했습니다</strong>
                <p>{error}</p>
                <span>입력한 키워드는 유지됩니다.</span>
              </div>
            )}
            <details className="competition-recent">
              <summary>
                <Clock3 size={15} />
                최근 자유 분석 <Badge>{recent.length}</Badge>
                <ChevronDown size={14} />
              </summary>
              <div>
                {recent.map((r, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => {
                      setArea(r.area);
                      setSubmitted(r.word);
                      setDraft(r.word);
                      setError("");
                      setRunning(false);
                    }}
                  >
                    <b>{r.word}</b>
                    <span>
                      {competitionAreas[r.area]} · {number(r.total, "건")}
                    </span>
                    <small>
                      {r.by} · {r.time}
                    </small>
                    <ArrowUpRight size={14} />
                  </button>
                ))}
              </div>
            </details>
          </Panel>
          {submitted && !error ? (
            <KeywordCompetitionView key={submitted + area} view={view} free />
          ) : (
            !error && (
              <Panel title="키워드를 바로 살펴보세요">
                <Empty title="조회할 키워드를 입력해 주세요">
                  고객사 등록 없이 상위 글의 공통점을 살펴볼 수 있습니다.
                </Empty>
              </Panel>
            )
          )}
        </>
      )}
      <p className="competition-footnote">
        공개 페이지의 집계 값만 비교합니다. 네이버의 순위 방식은 공개되지
        않았으며 이 결과는 순위의 원인을 설명하지 않습니다.
      </p>
    </div>
  );
}
